/* PAQEE drop-in sections — behaviour.
   Vanilla ES modules, no dependencies, no external requests.
   Every custom element degrades to readable static markup if this file fails. */

/* ---------- money ----------
   The shop's money_format is handed in on [data-money-format] so nothing here
   has to assume a currency. Mirrors Shopify's own formatMoney placeholders. */

const MONEY_DEFAULT = '&#8377;{{amount}}';

function formatMoney(cents, format) {
  const value = Number(cents);
  if (!Number.isFinite(value)) return '';
  const f = format || MONEY_DEFAULT;

  const group = (num, sep) => {
    // Indian grouping (1,23,456) when the separator is a comma and the number is
    // large enough for it to matter; plain thousands grouping otherwise.
    const [int, dec] = num.split('.');
    let head = int;
    let tail = '';
    if (sep === ',' && int.length > 3) {
      tail = int.slice(-3);
      head = int.slice(0, -3);
      head = head.replace(/\B(?=(\d{2})+(?!\d))/g, sep) + sep;
      return head + tail + (dec ? '.' + dec : '');
    }
    head = int.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
    return head + (dec ? '.' + dec : '');
  };

  const build = (decimals, sep) => group((value / 100).toFixed(decimals), sep);

  return f.replace(/\{\{\s*(\w+)\s*\}\}/, (_, name) => {
    switch (name) {
      case 'amount_no_decimals': return build(0, ',');
      case 'amount_with_comma_separator': return build(2, '.');
      case 'amount_no_decimals_with_comma_separator': return build(0, '.');
      case 'amount_with_apostrophe_separator': return build(2, "'");
      default: return build(2, ',');
    }
  });
}

function moneyFor(el, cents) {
  const host = el.closest('[data-money-format]');
  return formatMoney(cents, host ? host.dataset.moneyFormat : MONEY_DEFAULT);
}

/* ---------- variant picker ---------- */

class PqVariantPicker extends HTMLElement {
  connectedCallback() {
    const json = this.querySelector('[data-pq-variants]');
    try {
      this.variants = JSON.parse(json.textContent);
    } catch (e) {
      this.variants = [];
      return;
    }
    this.addEventListener('change', (e) => {
      if (e.target.matches('[data-pq-option]')) this.select();
    });
  }

  get chosen() {
    return Array.from(this.querySelectorAll('[data-pq-option]:checked')).map((i) => i.value);
  }

  select() {
    const chosen = this.chosen;
    const variant = this.variants.find((v) => v.options.every((o, i) => o === chosen[i]));

    this.querySelectorAll('.pq-chip').forEach((chip) => {
      const input = chip.querySelector('[data-pq-option]');
      chip.classList.toggle('is-selected', !!(input && input.checked));
    });
    this.querySelectorAll('[data-pq-chosen]').forEach((el) => {
      el.textContent = chosen[Number(el.dataset.pqChosen)] || '';
    });

    if (!variant) return;

    const form = this.dataset.form ? document.getElementById(this.dataset.form) : this.closest('form');
    const idInput = form && form.querySelector('[name="id"]');
    if (idInput) {
      idInput.value = variant.id;
      idInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    if (this.dataset.url && window.history.replaceState) {
      window.history.replaceState({}, '', `${this.dataset.url}?variant=${variant.id}`);
    }

    document.dispatchEvent(new CustomEvent('pq:variant-change', { detail: { variant } }));
  }
}

/* ---------- quantity stepper ---------- */

class PqStepper extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input');
    if (!this.input) return;
    this.addEventListener('click', (e) => {
      const step = e.target.closest('[data-pq-step]');
      if (!step) return;
      e.preventDefault();
      this.nudge(Number(step.dataset.pqStep));
    });
    this.input.addEventListener('change', () => this.nudge(0));
  }

  nudge(by) {
    const min = Number(this.input.min || 1);
    const max = this.input.max ? Number(this.input.max) : Infinity;
    const next = Math.min(max, Math.max(min, (Number(this.input.value) || min) + by));
    this.input.value = next;
    this.input.dispatchEvent(new Event('input', { bubbles: true }));
    document.dispatchEvent(new CustomEvent('pq:quantity-change', { detail: { quantity: next } }));
  }
}

/* ---------- product rate summary ----------
   Restates the live per-piece rate, the piece count and the order total as the
   shopper changes variant or quantity. Numbers come from the real variant
   prices, never from a rate table baked into the theme. */

class PqRateSummary extends HTMLElement {
  connectedCallback() {
    this.unit = Number(this.dataset.unit || 0);
    this.pack = Number(this.dataset.pack || 1);
    this.noun = this.dataset.noun || 'pc';

    document.addEventListener('pq:variant-change', (e) => {
      this.unit = e.detail.variant.unit;
      this.pack = e.detail.variant.pack || 1;
      this.render();
    });
    document.addEventListener('pq:quantity-change', () => this.render());
    this.render();
  }

  get quantity() {
    const source = this.dataset.quantityFrom
      ? document.querySelector(this.dataset.quantityFrom)
      : document.querySelector('[name="quantity"]');
    return Math.max(1, Number(source && source.value) || 1);
  }

  render() {
    const qty = this.quantity;
    const pieces = this.pack * qty;
    const total = this.unit * pieces;

    const set = (key, text) => {
      const el = this.querySelector(`[data-pq-${key}]`);
      if (el) el.textContent = text;
    };

    set('unit', `${moneyFor(this, this.unit)}/${this.noun}`);
    set('pieces', pieces.toLocaleString('en-IN'));
    set('packs', qty.toLocaleString('en-IN'));
    set('pack-size', this.pack.toLocaleString('en-IN'));
    set('total', moneyFor(this, total));
  }
}

/* ---------- product tabs ---------- */

class PqTabs extends HTMLElement {
  connectedCallback() {
    this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
    this.addEventListener('click', (e) => {
      const tab = e.target.closest('[role="tab"]');
      if (tab) this.show(tab);
    });
    this.addEventListener('keydown', (e) => {
      const i = this.tabs.indexOf(document.activeElement);
      if (i < 0) return;
      let next = null;
      if (e.key === 'ArrowRight') next = this.tabs[(i + 1) % this.tabs.length];
      if (e.key === 'ArrowLeft') next = this.tabs[(i - 1 + this.tabs.length) % this.tabs.length];
      if (e.key === 'Home') next = this.tabs[0];
      if (e.key === 'End') next = this.tabs[this.tabs.length - 1];
      if (!next) return;
      e.preventDefault();
      next.focus();
      this.show(next);
    });
  }

  show(tab) {
    this.tabs.forEach((t) => {
      const on = t === tab;
      t.setAttribute('aria-selected', String(on));
      t.tabIndex = on ? 0 : -1;
      const panel = document.getElementById(t.getAttribute('aria-controls'));
      if (panel) panel.hidden = !on;
    });
  }
}

/* ---------- size finder ----------
   Nine use cases, one answer. Every answer panel is rendered by Liquid from real
   variant prices, so this only swaps which one is visible — and with JS off the
   use cases stay ordinary links to the right variant. */

class PqSizeFinder extends HTMLElement {
  connectedCallback() {
    this.options = Array.from(this.querySelectorAll('[data-pq-case]'));
    this.panels = Array.from(this.querySelectorAll('[data-pq-panel]'));
    if (!this.options.length || !this.panels.length) return;

    this.addEventListener('click', (e) => {
      const option = e.target.closest('[data-pq-case]');
      if (!option) return;
      e.preventDefault();
      this.show(option.dataset.pqCase);
    });

    this.addEventListener('keydown', (e) => {
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      const i = this.options.indexOf(document.activeElement);
      if (i < 0) return;
      e.preventDefault();
      const step = e.key === 'ArrowDown' ? 1 : -1;
      const next = this.options[(i + step + this.options.length) % this.options.length];
      next.focus();
      this.show(next.dataset.pqCase);
    });

    this.show(this.options[0].dataset.pqCase);
  }

  show(key) {
    this.options.forEach((o) => {
      o.classList.toggle('is-selected', o.dataset.pqCase === key);
      o.setAttribute('aria-current', String(o.dataset.pqCase === key));
    });
    this.panels.forEach((p) => { p.hidden = p.dataset.pqPanel !== key; });
  }
}

/* ---------- bulk quote form ----------
   Repeatable line rows. Rows are plain inputs so the whole thing posts through
   Shopify's own contact form — no app, no endpoint of our own. */

class PqQuoteRows extends HTMLElement {
  connectedCallback() {
    this.list = this.querySelector('[data-pq-rows]');
    this.template = this.querySelector('template');
    this.max = Number(this.dataset.max || 12);

    // The catalogue index: every product with its own size and pack values.
    try {
      const raw = JSON.parse(this.querySelector('[data-pq-catalogue]').textContent);
      this.catalogue = new Map(raw.map((p) => [p.title, p]));
    } catch (e) {
      this.catalogue = new Map();
    }

    this.addEventListener('click', (e) => {
      if (e.target.closest('[data-pq-add]')) { e.preventDefault(); this.add(); return; }
      const remove = e.target.closest('[data-pq-remove]');
      if (remove) {
        e.preventDefault();
        const row = remove.closest('[data-pq-row]');
        if (row && this.rows.length > 1) { row.remove(); this.renumber(); }
      }
    });

    // Choosing a product repopulates that row's size and pack from real data.
    this.addEventListener('change', (e) => {
      if (e.target.matches('[data-pq-role="product"]')) this.cascade(e.target.closest('[data-pq-row]'));
    });

    this.rows.forEach((r) => this.cascade(r));
    this.renumber();
  }

  get rows() { return Array.from(this.list.querySelectorAll('[data-pq-row]')); }

  /* Fill size and pack for one row from the chosen product. A product with no
     such option leaves that select disabled rather than showing empty choices. */
  cascade(row) {
    if (!row) return;
    const product = row.querySelector('[data-pq-role="product"]');
    const entry = this.catalogue.get(product ? product.value : '');

    [['size', 'Size'], ['pack', 'Pack']].forEach(([role, label]) => {
      const sel = row.querySelector(`[data-pq-role="${role}"]`);
      if (!sel) return;
      const values = entry && entry[role + 's'] ? entry[role + 's'].split('~').filter(Boolean) : [];
      const previous = sel.value;
      sel.innerHTML = '';
      const blank = document.createElement('option');
      blank.value = '';
      blank.textContent = values.length ? label : `No ${label.toLowerCase()} options`;
      sel.appendChild(blank);
      values.forEach((v) => {
        const o = document.createElement('option');
        o.value = v;
        o.textContent = v;
        sel.appendChild(o);
      });
      sel.disabled = values.length === 0;
      if (values.includes(previous)) sel.value = previous;
    });
  }

  add() {
    if (this.rows.length >= this.max) return;
    this.list.appendChild(this.template.content.cloneNode(true));
    const last = this.rows[this.rows.length - 1];
    this.cascade(last);
    this.renumber();
    const first = last && last.querySelector('select, input');
    if (first) first.focus();
  }

  /* Field names are rewritten on every add and remove, so what lands in the
     inbox is always numbered 01, 02, 03 with no gaps. */
  renumber() {
    this.rows.forEach((row, i) => {
      const n = String(i + 1).padStart(2, '0');
      row.querySelectorAll('[data-pq-name]').forEach((f) => {
        f.name = `contact[Item ${n} ${f.dataset.pqName}]`;
      });
      const remove = row.querySelector('[data-pq-remove]');
      if (remove) remove.hidden = this.rows.length < 2;
    });
    const add = this.querySelector('[data-pq-add]');
    if (add) add.hidden = this.rows.length >= this.max;
  }
}

/* ---------- mobile buy bar ----------
   Appears once the real add-to-cart button has scrolled out of view. */

class PqBuyBar extends HTMLElement {
  connectedCallback() {
    const anchor = document.querySelector(this.dataset.watch || '[name="add"]');
    if (!anchor || !('IntersectionObserver' in window)) {
      this.hidden = false;
      return;
    }
    this.hidden = true;
    new IntersectionObserver(
      ([entry]) => { this.hidden = entry.isIntersecting; },
      { rootMargin: '0px 0px -20% 0px' }
    ).observe(anchor);
  }
}

/* ---------- rate ladder ----------
   Homepage matrix: pick a size and a pack, read the per-piece rate straight off
   the variant prices rendered into the table. */

class PqRateLadder extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', (e) => {
      const cell = e.target.closest('[data-pq-cell]');
      if (!cell) return;
      this.querySelectorAll('[data-pq-cell]').forEach((c) => c.classList.remove('is-selected'));
      cell.classList.add('is-selected');
      const out = this.querySelector('[data-pq-ladder-out]');
      if (out) {
        out.textContent = cell.dataset.pqCell;
        out.closest('[data-pq-ladder-note]')?.removeAttribute('hidden');
      }
      const cta = this.querySelector('[data-pq-ladder-cta]');
      if (cta && cell.dataset.pqUrl) cta.href = cell.dataset.pqUrl;
    });
  }
}

/* ---------- cart extras ----------
   Nudges the shopper to the next pack tier and keeps the GSTIN attribute in the
   cart in step with the field. */

class PqCartExtras extends HTMLElement {
  connectedCallback() {
    const gstin = this.querySelector('[data-pq-gstin]');
    if (!gstin) return;
    let timer;
    gstin.addEventListener('input', () => {
      clearTimeout(timer);
      timer = setTimeout(() => this.saveGstin(gstin), 500);
    });
  }

  async saveGstin(field) {
    const status = this.querySelector('[data-pq-gstin-status]');
    const value = field.value.trim().toUpperCase();
    field.value = value;

    // 15 characters: 2 state + 10 PAN + 3. Empty clears the attribute.
    if (value && !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(value)) {
      field.setAttribute('aria-invalid', 'true');
      if (status) status.textContent = 'That does not look like a 15-character GSTIN.';
      return;
    }
    field.removeAttribute('aria-invalid');

    try {
      await fetch(`${window.Shopify?.routes?.root || '/'}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: { GSTIN: value } })
      });
      if (status) status.textContent = value ? 'Saved — your invoice will carry this GSTIN.' : '';
    } catch (e) {
      if (status) status.textContent = 'Could not save that just now.';
    }
  }
}

/* ---------- product page ----------
   One element owns the whole buy column: option chips resolve a variant from
   the rendered JSON, and price, per-unit rate, pack count, quantity hint, order
   total, add-to-cart state and the URL all follow from it. Nothing is computed
   from a rate table — every figure derives from the variant's real price. */

class PqPdp extends HTMLElement {
  connectedCallback() {
    try {
      this.variants = JSON.parse(this.querySelector('[data-pq-variants]').textContent);
    } catch (e) {
      this.variants = [];
    }
    this.form = this.dataset.form ? document.getElementById(this.dataset.form) : this.querySelector('form');

    // Remember the in-stock label; swapping to "Out of stock" must be reversible.
    this.addBtn = this.querySelector('[data-pq-add]');
    if (this.addBtn) this.addLabel = this.addBtn.textContent.trim();

    this.addEventListener('click', (e) => {
      const chip = e.target.closest('[data-pq-opt]');
      if (chip && !chip.disabled) { this.pick(chip); return; }

      const step = e.target.closest('[data-pq-step]');
      if (step) { e.preventDefault(); this.nudge(Number(step.dataset.pqStep)); return; }

      const thumb = e.target.closest('[data-pq-thumb]');
      if (thumb) { e.preventDefault(); this.showImage(thumb); return; }

      if (e.target.closest('[data-pq-more]')) { e.preventDefault(); this.revealRest(); }
    });

    const qty = this.querySelector('[data-pq-qty]');
    if (qty) qty.addEventListener('input', () => this.render());

    if (this.form) this.form.addEventListener('submit', (e) => this.add(e));

    this.reveal();
    this.render();
  }

  get chosen() {
    const out = [];
    this.querySelectorAll('[data-pq-opt][aria-pressed="true"]').forEach((b) => {
      out[Number(b.dataset.pqOpt)] = b.dataset.pqValue;
    });
    return out;
  }

  get quantity() {
    const q = this.querySelector('[data-pq-qty]');
    return Math.max(1, Number(q && q.value) || 1);
  }

  pick(chip) {
    const pos = chip.dataset.pqOpt;
    this.querySelectorAll(`[data-pq-opt="${pos}"]`).forEach((b) => b.setAttribute('aria-pressed', String(b === chip)));
    this.render();
  }

  nudge(by) {
    const q = this.querySelector('[data-pq-qty]');
    if (!q) return;
    q.value = Math.max(1, (Number(q.value) || 1) + by);
    this.render();
  }

  get stage() {
    // Shopify's image_tag filter strips unknown data-* attributes, so the stage
    // is found by its container, never by a data hook.
    return this.querySelector('.pq-gal__stage img');
  }

  showImage(thumb) {
    const stage = this.stage;
    if (!stage || !thumb || !thumb.dataset.pqSrc) return;

    this.querySelectorAll('[data-pq-thumb]').forEach((t) => t.setAttribute('aria-current', String(t === thumb)));

    const src = thumb.dataset.pqSrc;
    if (stage.dataset.pqCurrent === src) return;

    // Record intent immediately; a slower earlier image must not overwrite a
    // faster later one, so every load checks it still owns the stage.
    stage.dataset.pqCurrent = src;

    // srcset/sizes are picked over src by the browser, so they go first.
    stage.removeAttribute('srcset');
    stage.removeAttribute('sizes');

    const next = new Image();
    next.onload = () => {
      if (stage.dataset.pqCurrent !== src) return;
      stage.classList.add('is-swapping');
      stage.src = src;
      setTimeout(() => stage.classList.remove('is-swapping'), 30);
    };
    next.onerror = () => { stage.classList.remove('is-swapping'); };
    next.src = src;
  }

  revealRest() {
    this.querySelectorAll('[data-pq-overflow]').forEach((t) => t.removeAttribute('hidden'));
    const more = this.querySelector('[data-pq-more]');
    if (more) more.remove();
  }

  /* Reveal the detail bands as they arrive, once each, never on a reduced-motion
     setting. Falls back to simply showing everything if IO is unavailable. */
  reveal() {
    const bands = document.querySelectorAll('.pq-det__band');
    if (!bands.length) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (!('IntersectionObserver' in window)) return;

    const show = (el) => { el.classList.add('is-in'); };

    bands.forEach((b) => {
      // Never hide something the reader can already see — a deep link or a
      // restored scroll position must not land on invisible content.
      const r = b.getBoundingClientRect();
      if (r.top < window.innerHeight && r.bottom > 0) return;
      b.classList.add('pq-reveal');
    });

    const hidden = [...bands].filter((b) => b.classList.contains('pq-reveal'));
    if (!hidden.length) return;

    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => {
        if (!en.isIntersecting) return;
        show(en.target);
        io.unobserve(en.target);
      });
    }, { rootMargin: '0px 0px -5% 0px', threshold: 0 });
    hidden.forEach((b) => io.observe(b));

    // Failsafe: whatever happens with the observer, nothing stays invisible.
    setTimeout(() => { hidden.forEach(show); io.disconnect(); }, 2500);
  }

  /* Add to cart without leaving the page, and say so on the button. Falls back
     to a normal form POST if the request fails for any reason. */
  async add(e) {
    if (!this.addBtn || this.addBtn.disabled) return;
    e.preventDefault();

    const restore = this.addLabel;
    this.addBtn.classList.add('is-busy');
    this.addBtn.textContent = 'Adding…';

    try {
      const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        body: new FormData(this.form),
        headers: { Accept: 'application/json' }
      });
      if (!res.ok) throw new Error(String(res.status));

      this.addBtn.classList.remove('is-busy');
      this.addBtn.classList.add('is-done');
      this.addBtn.textContent = 'Added to cart ✓';

      // Let the theme's own cart chrome update itself.
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true }));
      this.refreshCartCount();

      clearTimeout(this._t);
      this._t = setTimeout(() => {
        this.addBtn.classList.remove('is-done');
        this.addBtn.textContent = restore;
      }, 2200);
    } catch (err) {
      this.addBtn.classList.remove('is-busy');
      this.addBtn.textContent = restore;
      this.form.submit();   // let Shopify handle it the ordinary way
    }
  }

  async refreshCartCount() {
    try {
      const cart = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`).then((r) => r.json());
      document.querySelectorAll('[data-pq-cartcount]').forEach((el) => { el.textContent = cart.item_count; });
      document.querySelectorAll('[data-cart-count], .cart-count, [id*="cart-icon-bubble"] span')
        .forEach((el) => { if (/^\d+$/.test(el.textContent.trim())) el.textContent = cart.item_count; });
    } catch (e) { /* the badge is cosmetic; a stale count is not worth an error */ }
  }

  flash(sel, cls) {
    const el = this.querySelector(sel);
    if (!el) return;
    el.classList.remove(cls);
    void el.offsetWidth;
    el.classList.add(cls);
    setTimeout(() => el.classList.remove(cls), 600);
  }

  render() {
    const chosen = this.chosen;
    const variant = this.variants.find((v) => v.options.every((o, i) => o === chosen[i])) || null;
    const qty = this.quantity;

    const set = (key, text) => { const el = this.querySelector(`[data-pq-${key}]`); if (el) el.textContent = text; };

    if (variant) {
      const pack = variant.pack || 1;
      const noun = this.dataset.noun || 'pc';

      set('price', moneyFor(this, variant.price));
      set('perunit', `${moneyFor(this, variant.unit)} / ${noun}`);
      set('packcount', pack.toLocaleString('en-IN'));
      set('total', moneyFor(this, variant.price * qty));
      set('qtyhint', `${(pack * qty).toLocaleString('en-IN')} ${noun}${pack * qty === 1 ? '' : 's'} total`);
      if (variant.sku) set('sku', `SKU ${variant.sku}`);

      const id = this.form && this.form.querySelector('[data-pq-id]');
      if (id) { id.value = variant.id; id.dispatchEvent(new Event('change', { bubbles: true })); }

      if (this.addBtn) {
        this.addBtn.disabled = !variant.available;
        this.addBtn.textContent = variant.available ? this.addLabel : 'Out of stock';
      }

      if (variant.media) {
        const t = this.querySelector(`[data-pq-thumb="${variant.media}"]`);
        if (t) {
          // A variant image living past the +N cut still needs to be reachable.
          if (t.hasAttribute('hidden')) this.revealRest();
          this.showImage(t);
        }
      }

      if (this._last && this._last !== variant.id) {
        this.flash('[data-pq-price]', 'is-changed');
        this.flash('[data-pq-total]', 'is-changed');
      }
      this._last = variant.id;

      // Mark the size the shopper is on, in the rate table further down.
      const size = chosen[0];
      document.querySelectorAll('.pq-rates tbody tr').forEach((tr) => {
        const cell = tr.querySelector('th');
        tr.classList.toggle('is-current', !!cell && cell.textContent.trim() === size);
      });

      if (this.dataset.url && window.history.replaceState) {
        window.history.replaceState({}, '', `${this.dataset.url}?variant=${variant.id}`);
      }
      document.dispatchEvent(new CustomEvent('pq:variant-change', { detail: { variant } }));
    } else {
      // No such combination — say so rather than pricing something that cannot be bought.
      if (this.addBtn) { this.addBtn.disabled = true; this.addBtn.textContent = 'Unavailable'; }
    }
  }
}

/* ---------- collection filters ----------
   Submit on change so filtering feels immediate, while the form still works
   as an ordinary GET with JavaScript off. */

class PqFilters extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form') || this.closest('form');
    if (!this.form) return;

    this.form.addEventListener('change', (e) => {
      // Derived facets filter in place; native ones round-trip to Shopify.
      if (e.target.matches('[data-pq-facet]')) { this.applyFacets(); return; }
      if (e.target.matches('input[type="checkbox"]')) this.submit();
    });

    this.addEventListener('click', (e) => {
      const more = e.target.closest('[data-pq-more-filters]');
      if (!more) return;
      e.preventDefault();
      more.closest('.pq-filter').querySelectorAll('[data-pq-extra]').forEach((li) => li.removeAttribute('hidden'));
      more.remove();
    });

    // Price inputs settle before we navigate.
    let t;
    this.form.addEventListener('input', (e) => {
      if (!e.target.matches('input[type="number"]')) return;
      clearTimeout(t);
      t = setTimeout(() => this.submit(), 600);
    });
  }

  /* Size / Pack / Micron / Print are derived from the products because the store
     has no native option filters. Matching happens against data- attributes on
     each grid item, so it is instant and needs no round trip. Within a group the
     values are OR'd; across groups they are AND'd, which is how shoppers expect
     facets to behave. */
  applyFacets() {
    const chosen = {};
    this.querySelectorAll('[data-pq-facet]:checked').forEach((i) => {
      (chosen[i.dataset.pqFacet] ||= []).push(i.value.toLowerCase());
    });

    this.querySelectorAll('.pq-filter__chip').forEach((c) => {
      const i = c.querySelector('input');
      c.classList.toggle('is-on', !!(i && i.checked));
    });

    const grid = document.querySelector('.pq-coll__grid');
    const cards = grid ? [...grid.querySelectorAll('[data-pq-card]')] : [];
    const groups = Object.keys(chosen);
    let shown = 0;

    cards.forEach((card) => {
      const ok = groups.every((g) => {
        const have = (card.dataset['facet' + g.charAt(0).toUpperCase() + g.slice(1)] || '')
          .toLowerCase().split('~').map((v) => v.trim());
        return chosen[g].some((want) => have.includes(want));
      });
      card.hidden = !ok;
      if (ok) shown += 1;
    });

    const count = document.querySelector('[data-pq-count]');
    if (count) {
      const total = Number(count.dataset.pqTotal) || cards.length;
      const n = groups.length ? shown : total;
      count.textContent = `${n} ${n === 1 ? 'product' : 'products'}`;
    }

    // An empty result needs to say so rather than look like a broken page.
    let empty = grid && grid.parentElement.querySelector('[data-pq-facet-empty]');
    if (groups.length && shown === 0) {
      if (!empty && grid) {
        empty = document.createElement('div');
        empty.className = 'pq-card pq-coll__empty';
        empty.setAttribute('data-pq-facet-empty', '');
        empty.innerHTML = '<p>No products match those filters.</p>';
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'pq-btn pq-btn--tertiary';
        btn.textContent = 'Clear these filters';
        btn.addEventListener('click', () => {
          this.querySelectorAll('[data-pq-facet]:checked').forEach((i) => { i.checked = false; });
          this.applyFacets();
        });
        empty.appendChild(btn);
        grid.after(empty);
      }
      if (grid) grid.hidden = true;
    } else {
      if (empty) empty.remove();
      if (grid) grid.hidden = false;
    }
  }

  submit() {
    // Drop empty params so the URL stays readable and shareable.
    const params = new URLSearchParams();
    this.form.querySelectorAll('[name]').forEach((el) => {
      if (el.hasAttribute('data-pq-facet')) return;          // client-side only
      if (el.type === 'checkbox' && !el.checked) return;
      if (String(el.value).trim() === '') return;
      params.append(el.name, el.value);
    });
    const q = params.toString();
    window.location.href = window.location.pathname + (q ? '?' + q : '');
  }
}

/* ---------- header ---------- */

class PqHeaderMenu extends HTMLElement {
  connectedCallback() {
    this.burger = this.querySelector('[data-pq-burger]');
    this.drawer = this.querySelector('.pq-head__drawer');
    if (!this.burger || !this.drawer) return;
    this.burger.addEventListener('click', () => {
      const open = this.burger.getAttribute('aria-expanded') === 'true';
      this.burger.setAttribute('aria-expanded', String(!open));
      this.drawer.hidden = open;
    });
  }
}

const elements = {
  'pq-variant-picker': PqVariantPicker,
  'pq-stepper': PqStepper,
  'pq-rate-summary': PqRateSummary,
  'pq-tabs': PqTabs,
  'pq-size-finder': PqSizeFinder,
  'pq-quote-rows': PqQuoteRows,
  'pq-buy-bar': PqBuyBar,
  'pq-rate-ladder': PqRateLadder,
  'pq-cart-extras': PqCartExtras,
  'pq-pdp': PqPdp,
  'pq-filters': PqFilters,
  'pq-header': PqHeaderMenu
};

Object.entries(elements).forEach(([name, ctor]) => {
  if (!customElements.get(name)) customElements.define(name, ctor);
});
